/**
 * Per-tool merchant-facing summaries.
 *
 * The chat exposes ~40 agent tools across the loaded skills. Their
 * raw names and JSON inputs/outputs are useful to engineers and
 * useless — sometimes confusing — to merchants. This registry maps
 * each tool to a friendly one-line label that the chat surfaces in
 * place of the raw "Ran <kebab-name>" header.
 *
 * Three tiers:
 *   - **Tier 0 — hidden**: `label` returns `null`. The tool is
 *     dropped from the tool-group entirely. Background / agent-
 *     internal calls the merchant gains nothing from seeing (skill
 *     loading, catalog look-ups, doc searches, knowledge-base
 *     queries).
 *   - **Tier 1 — friendly one-liner**: `label` returns a string
 *     extracted from the tool's input/output. The merchant sees one
 *     human sentence and no expansion body.
 *   - **Tier 2 — rich card**: handled by `tool-renderers/index.tsx`;
 *     the registered card renders the output inline. Tier-2 tools
 *     also get a Tier-1 label here as a fallback (used when the card
 *     declines to render — e.g. unrecognised output shape).
 *
 * Adding a new tool: write one entry below keyed by the agent-side
 * tool name. Use the small `read*` helpers to extract typed fields
 * from `unknown` inputs/outputs without ad-hoc casting.
 */

export interface ToolSummary {
  /** Tier 0 — when true, the tool is dropped from the chat entirely.
   *  Used for background/agent-internal calls the merchant gains
   *  nothing from seeing. When set, `label` is unused. */
  hidden?: true;
  /** One-line label for the tool-group row. Receives the agent's
   *  input + (when available) output. */
  label: (input: unknown, output: unknown) => string | null;
}

// ---------- Field readers --------------------------------------------------
// These helpers extract typed fields from `unknown` inputs/outputs
// without sprinkling `as` casts through the registry entries.

function readField(obj: unknown, key: string): unknown {
  if (!isRecord(obj)) {
    return undefined;
  }
  return obj[key];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(obj: unknown, key: string): string | undefined {
  const v = readField(obj, key);
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readArrayLength(obj: unknown, key: string): number | undefined {
  const v = readField(obj, key);
  return Array.isArray(v) ? v.length : undefined;
}

function readBoolean(obj: unknown, key: string): boolean | undefined {
  const v = readField(obj, key);
  return typeof v === "boolean" ? v : undefined;
}

/** Normalise an order id/name to the merchant-facing `#1234` form.
 *  The agent's tools accept either `"1234"` or `"#1234"`; we always
 *  display with the `#` prefix. */
function orderTag(input: unknown): string | undefined {
  const raw =
    readString(input, "orderName") ??
    readString(input, "orderId") ??
    readString(input, "order");
  if (!raw) {
    return undefined;
  }
  return raw.startsWith("#") ? raw : `#${raw}`;
}

/** Last 6 chars of a return id — enough to disambiguate two open
 *  returns on the same order without leaking the full ObjectId. */
function returnIdTag(input: unknown): string | undefined {
  const id = readString(input, "returnId");
  if (!id) {
    return undefined;
  }
  return id.length > 6 ? `…${id.slice(-6)}` : id;
}

/** Bare hostname for a URL input (e.g. "https://www.nike.com/x" →
 *  "nike.com"), or undefined when the value isn't a parseable URL. */
function hostnameTag(input: unknown, key: string): string | undefined {
  const raw = readString(input, key);
  if (!raw) {
    return undefined;
  }
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

// ---------- Tier 0: hidden -------------------------------------------------
// Background calls the merchant gains nothing from seeing. Used as
// shorthand below in the registry. Declared as a const so the table
// reads as data, not branches.
const HIDDEN: ToolSummary = { hidden: true, label: () => null };

// ---------- The registry ---------------------------------------------------

export const TOOL_SUMMARIES: Record<string, ToolSummary> = {
  // -- Always-active / agent control ----------------------------------------
  load_skill: HIDDEN,
  search_skills: HIDDEN,
  // Background capability discovery — without an entry the fallback
  // renders internal jargon ("Ran Search code mode tools").
  search_code_mode_tools: HIDDEN,
  search_product_knowledge: HIDDEN,
  get_product_knowledge_page: HIDDEN,
  browse_product_knowledge: HIDDEN,
  // `escalate-to-human` renders as an approval confirmation card —
  // when the tool reaches output state it's already past the card
  // and we surface a simple completion line.
  "escalate-to-human": { label: () => "Connected you with support" },
  "churn-alert": { label: () => "Notified your account manager" },
  /** ~70 s, not sub-second like the rest, so the running tense shows.
   *  `output` is undefined until it settles — the only done signal. */
  verify_actual_behavior: {
    label: (_input, output) => {
      if (output === undefined) {
        // Measured 45-82 s: one static row for that long reads as broken
        // unless the row says to expect a wait.
        return "Checking behavior - this may take a minute";
      }
      // Failures report `verified: false` rather than throwing.
      return readBoolean(output, "verified") === true
        ? "Checked how this works"
        : "Couldn't confirm how this works";
    },
  },

  // -- Returns skill --------------------------------------------------------
  get_order: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Looked up order ${order}` : "Looked up an order";
    },
  },
  list_orders: {
    label: (_input, output) => {
      const n = readArrayLength(output, "orders");
      if (n === undefined) {
        return "Searched orders";
      }
      return n === 1 ? "Found 1 order" : `Found ${n} orders`;
    },
  },
  get_return_data: {
    // Tier 2 (ReturnSummaryCard) handles the rich body; this label
    // is the fallback when the card declines (or as the header text
    // if we ever wire one above the card).
    label: (input) => {
      const ret = returnIdTag(input);
      const order = orderTag(input);
      if (ret) {
        return `Looked up return ${ret}`;
      }
      if (order) {
        return `Looked up return for ${order}`;
      }
      return "Looked up a return";
    },
  },
  get_return_flow: { label: () => "Checked the return policy" },
  list_returns: {
    label: (_input, output) => {
      const n = readArrayLength(output, "returns");
      if (n === undefined) {
        return "Searched returns";
      }
      return n === 1 ? "Found 1 return" : `Found ${n} returns`;
    },
  },
  approve_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Approved return for ${order}` : "Approved the return";
    },
  },
  process_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Processed return for ${order}` : "Processed the return";
    },
  },
  update_return_status: {
    label: (input) => {
      const status = readString(input, "status");
      if (status) {
        return `Updated return to ${status.replace(/_/g, " ")}`;
      }
      return "Updated return status";
    },
  },
  reopen_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Reopened return for ${order}` : "Reopened the return";
    },
  },
  close_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Closed return for ${order}` : "Closed the return";
    },
  },
  reset_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Reset return for ${order}` : "Reset the return";
    },
  },
  create_return_comment: { label: () => "Added a comment to the return" },
  create_draft_return: {
    label: (input) => {
      const order = orderTag(input);
      return order
        ? `Started a draft return for ${order}`
        : "Started a draft return";
    },
  },
  manage_draft_return: {
    label: (input) => {
      const step = readString(input, "action") ?? readString(input, "step");
      if (step) {
        return `Configured draft return — ${step.replace(/_/g, " ")}`;
      }
      return "Configured the draft return";
    },
  },
  submit_draft_return: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Submitted return for ${order}` : "Submitted the return";
    },
  },
  list_return_tags: {
    label: (_input, output) => {
      const n = readArrayLength(output, "tags");
      if (n === undefined) {
        return "Listed your return tags";
      }
      return n === 1 ? "Found 1 tag" : `Found ${n} tags`;
    },
  },
  upsert_return_tag: {
    label: (input) => {
      const newName = readString(input, "newName");
      const name = readString(input, "name");
      if (newName && name && newName !== name) {
        return `Renamed tag "${name}" → "${newName}"`;
      }
      const display = newName ?? name;
      return display ? `Saved tag "${display}"` : "Saved a return tag";
    },
  },
  delete_return_tag: {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Deleted tag "${name}"` : "Deleted a return tag";
    },
  },
  carrier_claims: {
    label: (input, output) => {
      const order = orderTag(input);
      const n = readArrayLength(output, "claims");
      if (order && n !== undefined) {
        return n === 0
          ? `No carrier claims on ${order}`
          : `Checked carrier claims on ${order} — found ${n}`;
      }
      if (order) {
        return `Checked carrier claims on ${order}`;
      }
      return "Checked carrier claims";
    },
  },

  // -- Commerce Agent skill -----------------------------------------------------
  "get-commerce-agent-config": {
    label: () => "Checked Commerce Agent settings",
  },
  "set-commerce-agent-config": {
    label: () => "Updated Commerce Agent settings",
  },
  "list-commerce-agent-automations": {
    label: (_input, output) => {
      const n = readArrayLength(output, "automations");
      if (n === undefined) {
        return "Checked Commerce Agent automations";
      }
      return n === 1
        ? "Found 1 Commerce Agent automation"
        : `Found ${n} Commerce Agent automations`;
    },
  },
  "get-commerce-agent-automation": {
    label: () => "Pulled Commerce Agent automation details",
  },
  "list-support-v2-conversations": {
    label: (_input, output) => {
      const n = readArrayLength(output, "conversations");
      if (n === undefined) {
        return "Searched Commerce Agent support conversations";
      }
      return n === 1
        ? "Found 1 Commerce Agent support conversation"
        : `Found ${n} Commerce Agent support conversations`;
    },
  },
  "get-support-v2-conversation": {
    label: (_input, output) => {
      const conversation = readField(output, "conversation");
      const n = readArrayLength(conversation, "messages");
      if (n !== undefined) {
        return n === 1
          ? "Read Commerce Agent support conversation with 1 message"
          : `Read Commerce Agent support conversation with ${n} messages`;
      }
      return "Read Commerce Agent support conversation";
    },
  },
  "list-commerce-agent-conversations": {
    label: (_input, output) => {
      const n = readArrayLength(output, "conversations");
      if (n === undefined) {
        return "Searched Commerce Agent conversations";
      }
      return n === 1
        ? "Found 1 Commerce Agent conversation"
        : `Found ${n} Commerce Agent conversations`;
    },
  },
  "get-commerce-agent-conversation": {
    label: (_input, output) => {
      const conversation = readField(output, "conversation");
      const n = readArrayLength(conversation, "messages");
      if (n !== undefined) {
        return n === 1
          ? "Read Commerce Agent conversation with 1 message"
          : `Read Commerce Agent conversation with ${n} messages`;
      }
      return "Read Commerce Agent conversation";
    },
  },
  "get-commerce-agent-customer": { label: () => "Loaded customer details" },
  "get-ai-tone-and-voice-settings": {
    label: () => "Checked AI tone and voice",
  },
  "set-ai-tone-and-voice-settings": {
    label: () => "Updated AI tone and voice",
  },
  "get-brand-summary": { label: () => "Checked brand context" },
  searchKnowledge: HIDDEN,
  "search-knowledge": HIDDEN,
  "get-knowledge": { label: () => "Read Commerce Agent knowledge" },
  "upsert-knowledge": { label: () => "Saved Commerce Agent knowledge" },
  "delete-knowledge": { label: () => "Deleted Commerce Agent knowledge" },

  // -- OMS skill ------------------------------------------------------------
  list_automations: {
    label: (_input, output) => {
      const n = readArrayLength(output, "automations");
      if (n === undefined) {
        return "Checked your automations";
      }
      return n === 1 ? "Found 1 automation" : `Found ${n} automations`;
    },
  },
  get_order_overview: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Pulled details for ${order}` : "Pulled order details";
    },
  },
  get_rate: {
    label: (input) => {
      const order = orderTag(input);
      return order ? `Got a shipping rate for ${order}` : "Got a shipping rate";
    },
  },
  get_fulfillment_views: { label: () => "Listed your fulfillment views" },
  get_fulfillment_group_audit_log: {
    label: () => "Checked fulfillment audit log",
  },
  "get-views-summary": { label: () => "Listed your views" },
  "get-view-details": {
    label: (input) => {
      const name = readString(input, "viewName");
      return name ? `Pulled view "${name}"` : "Pulled view details";
    },
  },
  // Pre-flight catalog/values lookups — hidden so the merchant only
  // sees the action that follows (e.g. "Created view") instead of
  // the half-dozen calls the agent made to discover the schema.
  "get-filter-catalog": HIDDEN,
  "get-filter-details": HIDDEN,
  "get-filter-values": HIDDEN,
  "get-column-catalog": HIDDEN,
  "get-column-details": HIDDEN,
  "get-packing-slip-block-catalog": HIDDEN,
  "get-packing-slip-block-details": HIDDEN,
  "get-carrier-parcels": HIDDEN,
  "get-team-unit-system": HIDDEN,
  "get-fulfillment-service-names": HIDDEN,
  list_team_users: HIDDEN,
  printnode_docs_search: HIDDEN,
  "create-folder": {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Created folder "${name}"` : "Created a folder";
    },
  },
  "create-tag": {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Created tag "${name}"` : "Created a tag";
    },
  },
  "create-views": {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Created view "${name}"` : "Created a view";
    },
  },
  "update-view": {
    label: (input) => {
      const name = readString(input, "name") ?? readString(input, "viewName");
      return name ? `Updated view "${name}"` : "Updated a view";
    },
  },
  "get-packing-slip-templates": { label: () => "Listed your packing slips" },
  "get-packing-slip-template-detail": {
    label: () => "Pulled packing slip details",
  },
  "create-packing-slip-template": {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Created packing slip "${name}"` : "Created a packing slip";
    },
  },
  "update-packing-slip-template": { label: () => "Updated a packing slip" },
  "get-parcels": { label: () => "Listed your parcels" },
  "upsert-parcel": {
    label: (input) => {
      const name = readString(input, "name");
      return name ? `Saved parcel "${name}"` : "Saved a parcel";
    },
  },
  printer_tool: { label: () => "Checked printer status" },
  print_job_debug: { label: () => "Reviewed recent print jobs" },
  workstation_tool: {
    label: (input) => {
      const action = readString(input, "action");
      // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
      switch (action) {
        case "list_printers":
          return "Listed available printers";
        case "list_workstations":
          return "Listed workstations";
        case "create_workstation": {
          const name =
            readString(input, "name") ??
            readString(readField(input, "workstation"), "name");
          return name
            ? `Created workstation "${name}"`
            : "Created a workstation";
        }
        case "update_workstation": {
          const name =
            readString(input, "name") ??
            readString(readField(input, "workstation"), "name");
          return name
            ? `Updated workstation "${name}"`
            : "Updated a workstation";
        }
        default:
          return "Managed workstation";
      }
    },
  },

  // -- Analytics skill ------------------------------------------------------
  "get-return-analytics": { label: () => "Pulled return analytics" },
  "get-product-analytics": { label: () => "Pulled product analytics" },
  "get-variant-analytics": { label: () => "Pulled variant analytics" },
  "get-carrier-analytics": { label: () => "Pulled carrier performance" },
  "get-fulfillment-metrics": { label: () => "Pulled fulfillment metrics" },
  "get-orders-analytics": { label: () => "Pulled order analytics" },
  "get-shipment-details": { label: () => "Checked shipment details" },
  "generate-custom-analytics-chart": {
    label: (_input, output) => {
      const title = readString(readField(output, "chart"), "title");
      return title ? `Generated chart "${title}"` : "Generated a chart";
    },
  },

  // -- Portal Builder skill -------------------------------------------------
  "inspect-website-style": {
    label: (input) => {
      const host = hostnameTag(input, "url");
      return host
        ? `Inspected ${host}'s branding`
        : "Inspected the site's branding";
    },
  },
  "analyze-portal-asset": {
    label: (input) => {
      const assetType = readString(input, "assetType");
      const n = readArrayLength(input, "urls");
      if (assetType === "background") {
        return n !== undefined && n > 1
          ? `Reviewed ${n} background images`
          : "Reviewed a background image";
      }
      if (assetType === "logo") {
        return "Reviewed logo options";
      }
      if (assetType === "favicon") {
        return "Reviewed favicon options";
      }
      return "Reviewed image options";
    },
  },
  "upload-portal-asset": {
    label: (input) => {
      const assetType = readString(input, "assetType");
      if (assetType === "background") {
        return "Uploaded a background image";
      }
      if (assetType === "logo") {
        return "Uploaded the logo";
      }
      if (assetType === "favicon") {
        return "Uploaded the favicon";
      }
      return "Uploaded a portal image";
    },
  },
  "update-portal-customization": {
    // The agent supplies a short, merchant-facing `reason` describing
    // the edit — surface it verbatim so every style change reads as a
    // descriptive line rather than a generic label.
    label: (input) =>
      readString(input, "reason") ?? "Updated your portal styles",
  },
  // Rendered as its own structured question card once output arrives.
  // Showing a transient generic tool row before that card appears is
  // confusing because the merchant should only see the actual question.
  cppu__list: { label: () => "Listed your post-purchase pages" },
  cppu__get: { label: () => "Looked up a post-purchase page" },
  cppu__create: { label: () => "Created a post-purchase page" },
  cppu__update: { label: () => "Updated a post-purchase page" },
  cppu__duplicate: { label: () => "Duplicated a post-purchase page" },
  cppu__delete: { label: () => "Deleted a post-purchase page" },
  cppf__get: { label: () => "Read your post-purchase flow" },
  cppf__get_context: HIDDEN,
  cppf__draft_create: HIDDEN,
  cppf__draft_get: HIDDEN,
  cppf__draft_edit: HIDDEN,
  cppf__draft_commit: { label: () => "Updated your post-purchase flow" },
  pp__get_upsell_metrics: {
    label: () => "Pulled post-purchase upsell metrics",
  },
  pp__get_upsell_product_metrics: {
    label: () => "Pulled post-purchase metrics by product",
  },
  "ask-user-question": HIDDEN,
  // Rendered as its own capability card. The generic "Ran Show
  // capability block" row is implementation noise.
  show_capability_block: HIDDEN,
  // Rendered as its own clickable carousel card (see ask-redo-message
  // renderParts), so it never needs a tool-group row.
  "present-image-options": HIDDEN,
};

/**
 * Look up the registered summary for a tool. Returns `undefined` when
 * no entry exists — callers should fall back to a generic "Ran X"
 * label rather than treating the tool as hidden.
 */
export function getToolSummary(toolName: string): ToolSummary | undefined {
  return TOOL_SUMMARIES[toolName];
}

/**
 * Whether a tool should be hidden from the chat entirely. Unknown
 * tools default to visible (with a generic fallback label) so a new
 * agent-side tool doesn't silently disappear from the UI.
 */
export function isToolHidden(toolName: string): boolean {
  return TOOL_SUMMARIES[toolName]?.hidden === true;
}

/**
 * Resolve a tool's label given its agent input + output. Falls back
 * to a "Ran <prettified name>" string when no entry exists.
 */
export function formatToolLabel(
  toolName: string,
  input: unknown,
  output: unknown,
): string | null {
  const summary = TOOL_SUMMARIES[toolName];
  if (summary) {
    return summary.label(input, output);
  }
  // Unknown tools: turn `kebab-case` / `snake_case` into "Sentence
  // case" so the merchant sees something readable while the registry
  // catches up.
  const spaced = toolName.replace(/[-_]/g, " ");
  return spaced.length > 0
    ? `Ran ${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`
    : "Ran a tool";
}
